// Boot (TECH_BASELINE §2): fit khung → nạp save → quyết toán idle (Hồi Sơn) → dựng stage + vỏ.
import './ui/fonts.css';
import './ui/tokens.css';
import './ui/base.css';
import './ui/shell/shell.css';
import './ui/kit/kit.css';
import './ui/screens/screens.css';
import { render, h } from 'preact';
import { effect } from '@preact/signals';
import { injectColorTokens } from '@ui/tokens';
import { installFit, fitFrame } from '@ui/fit';
import { App } from '@ui/shell/App';
import { createSaveStore } from '@save/store';
import { idbKv, localStorageMirror, saveNamespace } from '@save/backends';
import { bindAutosave, bootSave, flush, markDirty } from '@state/persistence';
import { clockOffset, now, setClockOffset } from '@state/clock';
import { logLine } from '@state/modules/log';
import { settings } from '@state/modules/settings';
import {
  activeScreen,
  activeTab,
  ask,
  askConfirm,
  closeWindows,
  openWindow,
  selectScreen,
  selectTab,
  subpage,
  windows,
} from '@state/modules/ui';
import {
  breakthroughPulse,
  cultivation,
  lampRootFormed,
  onlineRate,
  resumeFromAbsence,
  rewindForDev,
  simulateAbsence,
  startIdleLoop,
} from '@state/modules/cultivation';
import { advanceOpening, openingStep, skipOpening } from '@state/modules/story';
import { addGoods, devGrantItems, inventory } from '@state/modules/inventory';
import { revealing, startReveal } from '@state/modules/reveal';
import { mountLootFx } from '@render/lootFx';
import { setBeamHook } from '@ui/shell/LootFx';
import { setRiteArt } from '@ui/shell/RevealRite';
import { bindAudioUnlock } from '@ui/audio';
import { rollItem, rollUnique } from '@sim/items/generate';
import { createRng } from '@sim/core/rng';
import { openSubpage } from '@state/modules/ui';
import { devLearnRecipe } from '@state/modules/cave';
import { dismissReturn } from '@state/modules/returnSummary';
import { createStage, setScenePaused } from '@render/stage';
import { mountQiField } from '@render/qiField';
import { mountCultivationScene } from '@render/cultivationScene';
import { mountScreenBackdrops } from '@render/screenBackdrop';
import { techniqueDef } from '@data/content/techniques';
import { attachPerfProbe } from '@render/perfProbe';
import { registerSW } from 'virtual:pwa-register';

function newSeed(): number {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0] ?? 1;
}

function applySettings(): void {
  effect(() => {
    const s = settings.value;
    const html = document.documentElement;
    html.classList.toggle('rm', s.reduceMotion);
    html.classList.toggle('hc', s.highContrast);
    html.style.setProperty('--text-k', String(s.textScale));
  });
}

/**
 * Kịch bản đo hiệu năng `?perf=shell-idle` (TECH_BASELINE §10): màn Tu Luyện đang bế quan (tick 1s,
 * hạt linh khí xoáy), mở/đóng cửa sổ Tử Quan mỗi 2s.
 */
function runPerfScenario(name: string): void {
  if (name === 'inventory-full') {
    // Hành Trang, túi đầy 36 món mọi phẩm (M2 acceptance): mở trang con Túi, đổi ngăn mỗi 2s.
    const slots = [
      'weapon',
      'helm',
      'robe',
      'pants',
      'gloves',
      'boots',
      'necklace',
      'ring',
      'treasure',
    ] as const;
    const rar = ['mortal', 'yellow', 'mystic', 'earth', 'heaven'] as const;
    devGrantItems(
      Array.from({ length: 36 }, (_, i) =>
        rollItem(
          {
            uid: `perf${i}`,
            slot: slots[i % slots.length]!,
            rarity: rar[i % rar.length]!,
            giai: 15,
            source: 'common',
            origin: 'res.langCoc',
            atMs: now(),
          },
          createRng(i + 1),
        ),
      ),
    );
    selectTab('character');
    selectScreen('inventory');
    openSubpage('bag');
    let k = 0;
    setInterval(
      () => document.querySelectorAll<HTMLElement>('[data-id^="bag-tab-"]')[k++ % 3]?.click(),
      2000,
    );
    return;
  }
  if (name === 'reveal') {
    // Hoạt ảnh Khai Bảo lặp: mỗi 4,5s nhận một độc bản mới vào Rương Chưa Mở rồi diễn lại.
    selectTab('character');
    selectScreen('inventory');
    let i = 0;
    const loop = (): void => {
      const u = rollUnique('unique.fallenLeafSword', 18, `perfu${i++}`, now(), createRng(i));
      devGrantItems([u]);
      startReveal(u.uid);
    };
    loop();
    setInterval(() => {
      const cur = revealing.peek();
      if (cur) {
        document.querySelector<HTMLElement>('[data-id="reveal"]')?.click();
        document.querySelector<HTMLElement>('[data-id="reveal-take"]')?.click();
      }
      loop();
    }, 4500);
    return;
  }
  if (name !== 'shell-idle') return;
  selectTab('character');
  selectScreen('cultivation');
  setInterval(() => {
    if (windows.value.length > 0) closeWindows();
    else openWindow({ kind: 'deathSeclusion' });
  }, 2000);
}

/** Màu vòng khí hải theo hướng tu = Tam Nguyên trội của công pháp chủ tu; cân nhau thì sắc giấy. */
function toneOf(techniqueId: string): number {
  const { body, spirit, mind } = techniqueDef(techniqueId).ratio;
  const top = Math.max(body, spirit, mind);
  const n = [body, spirit, mind].filter((x) => x === top).length;
  if (n > 1) return 0xf0e2be;
  return spirit === top ? 0x6fb6ee : body === top ? 0xe2856f : 0xf2dfa0;
}

async function boot(): Promise<void> {
  injectColorTokens();
  installFit();
  const params = new URLSearchParams(location.search);
  // Mỗi bản trên site ghép (/, /beta/, /beta/mX/) có kho save riêng (DECISIONS 2026-09-29).
  const ns = saveNamespace(import.meta.env.BASE_URL);
  const store = createSaveStore({
    kv: idbKv(ns.idbName),
    mirror: localStorageMirror(localStorage, ns.mirrorKey),
    now,
    newSeed,
  });
  const loaded = await bootSave(store);
  bindAutosave();
  bindAudioUnlock();
  // Kịch bản đo hiệu năng và e2e vào thẳng vòng idle, bỏ phần mở đầu (TECH_BASELINE §10).
  const perf = params.get('perf');
  if (perf || params.get('story') === 'skip') skipOpening();
  // Quyết toán quãng vắng TRƯỚC khi dựng màn (khung hình đầu đã đúng số); vắng ≥ 1 canh giờ → Hồi Sơn.
  resumeFromAbsence(now());
  if (perf) dismissReturn();
  startIdleLoop();
  markDirty();
  applySettings();

  const root = document.getElementById('app-root');
  if (!root) throw new Error('missing #app-root');
  render(h(App, {}), root);
  fitFrame();

  const app = createStage(document.body);
  const seed = Number(params.get('seed') ?? loaded.doc.seed) >>> 0;
  const field = mountQiField(app, seed);
  const probe = attachPerfProbe(app, () => field.count);
  // Màn Tu Luyện có scene riêng (người tu luyện + khí hải, theo màn 1a của client tham khảo); màn có
  // tranh nền trong client (Công Pháp) dùng lại rig đó;
  // màn khác chỉ còn hạt linh khí mờ làm nền. Phần mở đầu diễn trên scene Tu Luyện.
  const cult = mountCultivationScene(app, seed, import.meta.env.BASE_URL);
  const backdrops = mountScreenBackdrops(app, import.meta.env.BASE_URL);
  field.setFocus(false);
  effect(() => {
    const key = `${activeTab.value}.${activeScreen.value}`;
    const onCult = openingStep.value !== null || key === 'character.cultivation';
    cult.setVisible(onCult);
    backdrops.show(onCult ? null : key);
    // Hạt linh khí M0 chỉ còn làm nền cho màn chưa có tranh.
    field.root.visible = !onCult && !backdrops.has(key);
  });
  effect(() => cult.setIntensity(lampRootFormed.value ? 0.7 + 0.3 * Math.log2(1 + onlineRate.value) : 0.35));
  effect(() => cult.setTone(toneOf(cultivation.value.technique.mainId)));
  let lastPulse = breakthroughPulse.peek();
  effect(() => {
    const p = breakthroughPulse.value;
    if (p !== lastPulse) cult.pulse();
    lastPulse = p;
  });
  const lootFx = mountLootFx(app);
  setBeamHook((lv) => lootFx.beam(lv));
  setRiteArt(lootFx.reveal);
  // Scene bị lớp phủ che (cửa sổ, trang con, dải hỏi) thì dừng update + vẽ lại; Khai Bảo luôn chạy.
  effect(() =>
    setScenePaused(
      revealing.value === null && (windows.value.length > 0 || subpage.value !== null || ask.value !== null),
    ),
  );

  // Hook dev / test (TECH_BASELINE §9.4, UI_SPEC §15). Hook chỉ đọc (perf, render, save, state) luôn có;
  // hook đổi trạng thái (trao đồ, lệch giờ, giả vắng, tua mở đầu) chỉ gắn ở bản dev hoặc khi URL mang
  // tham số chế độ thử (`?dev`, `?perf=`, `?story=skip`: e2e / perf / tools/shot chạy trên bản build).
  const testHooks = import.meta.env.DEV || params.has('dev') || !!perf || params.get('story') === 'skip';
  Object.assign(window, {
    __ht: {
      perf: probe,
      render: { app, field, cult },
      save: { store, flush, loaded: { source: loaded.source, quarantined: loaded.quarantined } },
      cult: { state: () => cultivation.peek() },
      inv: { state: () => inventory.peek() },
    },
  });
  if (testHooks) attachTestHooks();

  // Save từ bản app mới hơn: chạy chỉ đọc, mời tải lại bản mới (TECH_BASELINE §7.2).
  if (loaded.readOnly)
    askConfirm({
      textKey: 'ui.save.readOnly',
      confirmKey: 'ui.save.reload',
      onConfirm: () => location.reload(),
    });

  if (perf) runPerfScenario(perf);
  else if (import.meta.env.PROD) registerUpdatePrompt();
}

/** Hook đổi trạng thái cho dev / e2e / perf: gộp vào `window.__ht` đã có, thêm `__clock`, `__return`. */
function attachTestHooks(): void {
  const w = window as unknown as { __ht: Record<string, Record<string, unknown>> };
  Object.assign(window, {
    __clock: { set: setClockOffset, get: clockOffset, now },
    __return: { simulate: simulateAbsence },
  });
  Object.assign(w.__ht.cult!, { resume: resumeFromAbsence, rewind: rewindForDev });
  Object.assign(w.__ht.inv!, {
    grant: devGrantItems,
    goods: addGoods,
    reveal: startReveal,
    roll: (rarity: 'mortal', slot: 'robe', i: number) =>
      rollItem(
        { uid: `dev${i}`, slot, rarity, giai: 15, source: 'common', origin: 'res.langCoc', atMs: now() },
        createRng(i + 1),
      ),
    unique: (id: string, i: number) => rollUnique(id, 18, `devu${i}`, now(), createRng(i + 1)),
  });
  w.__ht.story = { advance: advanceOpening, skip: skipOpening };
  w.__ht.cave = { learn: devLearnRecipe };
  w.__ht.log = {
    demo: (n: number) => {
      for (let i = 0; i < n; i++) logLine('system', 'log.system.demo', { n: i + 1 });
    },
  };
}

/** registerType 'prompt': có bản mới thì hỏi bằng dải hỏi, không tự reload (TECH_BASELINE §9.6). */
function registerUpdatePrompt(): void {
  const update = registerSW({
    onNeedRefresh: () =>
      askConfirm({
        textKey: 'ui.pwa.update',
        confirmKey: 'ui.save.reload',
        onConfirm: () => void update(true),
      }),
  });
}

void boot();
